import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import download from 'downloadjs';
import { styled } from 'react-free-style';
import { notify } from 'react-notify-toast';

import { Icons, colors } from '@united-talent-agency/components';
import { requestPromise } from '@united-talent-agency/julius-frontend-store';
import { gql, useMutation } from '@apollo/client';
import { useLogoutMutation } from '../../../generated/graphql';
import { getStoredGraphQLToken } from '../../../utils/getStoredGraphQLToken';

const SEND_PHONESHEET_EMAIL = gql`
  mutation SendPhonesheetEmail($input: SendPhonesheetEmailInput!) {
    sendPhonesheetEmail(input: $input) {
      success
      message
      emailId
    }
  }
`;

import ArchiveSearch from '../ArchiveSearch';
import {
  createShortcuts,
  filterInputEvent,
  keyboard,
  stringifyKey,
} from '../../../support/keyboard';
import { apiServerUrl } from '../../../support/urls';
import HeaderMenu from '../../../components/HeaderMenu';
import SettingsModalContent from '../../../components/SettingsModalContent';
import SearchBar from '../../../components/SearchBar';
import ReactModal from 'react-modal';
import ExportSettingsModal from '../../../components/ExportSettingsModal';
import { buildExportQuery } from '../../../utils/build-export-query';
import {
  useUserPhoneSheetExportSettingsQuery,
  useUpdateUserPhoneSheetPrintSettingsMutation,
  useUpdateUserPhoneSheetCsvSettingsMutation,
} from '../../../generated/graphql';

// `field` is both the user settings field and the key of its update mutation's input.
const EXPORT_FORMATS = {
  pdf: {
    field: 'phoneSheetPrintSettings',
    contentType: 'application/pdf',
    fileName: 'phone-sheet.pdf',
    title: 'Print Settings',
    confirmLabel: 'Print',
    successMessage: 'Printed successfully',
    saveFailedMessage: 'Phonesheet printed, but the settings could not be saved',
    errorMessage: 'Failed to print phonesheet',
  },
  csv: {
    field: 'phoneSheetCsvSettings',
    contentType: 'text/csv',
    fileName: 'phone-sheet.csv',
    title: 'CSV Settings',
    confirmLabel: 'Download',
    successMessage: 'Downloaded successfully',
    saveFailedMessage: 'Phonesheet downloaded, but the settings could not be saved',
    errorMessage: 'Failed to download CSV',
  },
};

const Component = ({
  user,
  desk,
  styles,
  search,
  onCallAdd,
  totalCounts,
  updateQuery,
  deskStatuses,
  deskStatusesDeskId,
  onReloadDeskStatuses,
}) => {
  // Refs
  const searchInput = useRef();

  // States
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isSendingPhonesheetEmail, setIsSendingPhonesheetEmail] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // GraphQL Mutations
  const [logoutMutation] = useLogoutMutation();

  const [sendPhonesheetEmailMutation] = useMutation(SEND_PHONESHEET_EMAIL);

  const {
    data: exportSettingsData,
    loading: exportSettingsLoading,
    error: exportSettingsError,
    refetch: refetchExportSettings,
  } = useUserPhoneSheetExportSettingsQuery({
    variables: { id: user?._id },
    skip: !user?._id,
  });
  const [settingsRetry, setSettingsRetry] = useState('idle');
  const settingsRetryRun = useRef(0);
  const [updatePrintSettings] = useUpdateUserPhoneSheetPrintSettingsMutation();
  const [updateCsvSettings] = useUpdateUserPhoneSheetCsvSettingsMutation();
  const updateSettingsByFormat = useMemo(
    () => ({ pdf: updatePrintSettings, csv: updateCsvSettings }),
    [updatePrintSettings, updateCsvSettings]
  );
  const exportConfig = EXPORT_FORMATS[exportFormat];
  const savedUserSettings = exportSettingsData?.users?.[0];
  const currentExportSettings =
    savedUserSettings?.[exportConfig?.field]?.find((s) => String(s.deskId) === String(desk?._id)) ||
    null;
  // deskStatuses is undefined until it loads, and keeps the previous desk's list while a desk
  // switch refetches; without this the dialog would show the wrong status list while silently
  // still applying the saved exclusions on confirm.
  const deskStatusesReady = !!deskStatuses && String(deskStatusesDeskId) === String(desk?._id);
  const awaitingSettingsRetry = !!exportSettingsError && settingsRetry !== 'done';
  const exportSettingsPending =
    exportSettingsLoading || awaitingSettingsRetry || !deskStatusesReady;

  // Retry a failed settings load once per open; if it fails again the dialog uses the defaults.
  useEffect(() => {
    if (!exportFormat) {
      settingsRetryRun.current += 1;
      setSettingsRetry('idle');
      return;
    }
    if (exportSettingsError && settingsRetry === 'idle') {
      const run = settingsRetryRun.current;
      setSettingsRetry('retrying');
      refetchExportSettings()
        .catch((error) => console.error('Failed to load export settings:', error))
        // A retry from an already closed dialog must not count for the next open.
        .finally(() => run === settingsRetryRun.current && setSettingsRetry('done'));
    }
  }, [exportFormat, exportSettingsError, refetchExportSettings, settingsRetry]);

  // The statuses load retries once on its own; if that also failed, opening a dialog tries again.
  useEffect(() => {
    if (exportFormat && !deskStatusesReady) {
      onReloadDeskStatuses?.();
    }
  }, [exportFormat, deskStatusesReady, onReloadDeskStatuses]);

  useEffect(() => {
    const keyboardListener = filterInputEvent(
      createShortcuts({
        [stringifyKey('/')]: (e) => {
          e.preventDefault();

          return searchInput.current?.focus && searchInput.current?.focus();
        },
      })
    );

    keyboard.addListener(keyboardListener);

    return () => {
      keyboard.removeListener(keyboardListener);
    };
  }, []);

  const closeSettings = () => {
    setIsSettingsModalOpen(false);
  };

  const handleOnSaveSettings = () => {
    closeSettings();
  };

  const openSettings = () => {
    setIsSettingsModalOpen(true);
  };

  const toggleArchive = useCallback(() => {
    setIsArchiveModalOpen((oldState) => !oldState);
  }, []);

  // TODO: just, no
  // send request to backend and let it handle
  // the querying there, not in frontend
  const requestFile = useCallback(
    async (contentType, fileName, params) => {
      const fileRequest = await requestPromise(
        user,
        apiServerUrl,
        '/call_todo',
        'GET',
        null,
        params,
        contentType
      );
      // requestPromise returns the raw fetch Response for pdf/csv, and fetch resolves on
      // HTTP errors — without this check a 404 body gets saved as the "file" and the
      // caller reports success.
      if (!fileRequest.ok) {
        throw new Error(`Download failed: the server responded with ${fileRequest.status}`);
      }
      const responseBlob = await fileRequest.blob();
      download(responseBlob, fileName, contentType);
    },
    [user]
  );

  const openPdfSettings = useCallback(() => setExportFormat('pdf'), []);
  const openCsvSettings = useCallback(() => setExportFormat('csv'), []);

  const handleExportWithSettings = useCallback(
    async (settings) => {
      if (isExporting) {
        return;
      }
      setIsExporting(true);
      // A failed save must not cost the user their export; it only turns the toast into a warning.
      // The mutation result isn't merged into the query (addTypename: false), so the awaited
      // refetch is what makes a reopen show the new value.
      // No loaded settings means the dialog showed defaults; saving them would wipe the real choice.
      let saveFailed = !savedUserSettings;
      const save = saveFailed
        ? Promise.resolve()
        : updateSettingsByFormat[exportFormat]({
            variables: { input: { [exportConfig.field]: [{ deskId: desk?._id, ...settings }] } },
            refetchQueries: ['UserPhoneSheetExportSettings'],
            awaitRefetchQueries: true,
          }).catch((error) => {
            saveFailed = true;
            console.error(`Failed to save ${exportFormat} settings:`, error);
          });

      try {
        const params = buildExportQuery({ deskId: desk?._id, settings });
        await requestFile(exportConfig.contentType, exportConfig.fileName, params);
        await save;

        setExportFormat(null);
        if (saveFailed) {
          notify.show(exportConfig.saveFailedMessage, 'warning');
        } else {
          notify.show(exportConfig.successMessage, 'success');
        }
      } catch (error) {
        // The export itself failed: keep the dialog open so the choice isn't lost.
        console.error(`Failed to export ${exportFormat}:`, error);
        notify.show(error.message || exportConfig.errorMessage, 'error');
        // Stay locked until this save lands, or it could overwrite a quicker retry's save.
        await save;
      } finally {
        setIsExporting(false);
      }
    },
    [
      desk?._id,
      exportConfig,
      exportFormat,
      isExporting,
      requestFile,
      savedUserSettings,
      updateSettingsByFormat,
    ]
  );

  const requestEmail = useCallback(async () => {
    try {
      const { data } = await sendPhonesheetEmailMutation({
        variables: {
          input: {
            deskId: desk?._id,
            timezone: new Date().getTimezoneOffset(),
          },
        },
      });
      return data.sendPhonesheetEmail;
    } catch (error) {
      console.error('Error sending phonesheet email:', error);
      return { success: false, message: error.message };
    }
  }, [desk?._id, sendPhonesheetEmailMutation]);

  const sendEmail = useCallback(async () => {
    setIsSendingPhonesheetEmail(true);
    const data = await requestEmail();
    if (data.success) {
      notify.show('Email Sent!', 'success');
    } else {
      notify.show('Email failed to send...', 'error');
    }
    setIsSendingPhonesheetEmail(false);
  }, [requestEmail]);

  const emailPhonesheet = useCallback(() => {
    if (!isSendingPhonesheetEmail) {
      sendEmail();
    }
  }, [isSendingPhonesheetEmail, sendEmail]);

  const signOut = useCallback(async () => {
    try {
      // Call GraphQL logout mutation first
      const userLoginToken = getStoredGraphQLToken();
      if (userLoginToken) {
        await logoutMutation({
          variables: {
            email: user.email,
            currentToken: userLoginToken,
            loginInterface: 'WEB',
          },
        });
        console.log('GraphQL logout successful');
      }
    } catch (error) {
      console.warn('GraphQL logout failed:', error);
      // Continue with local logout even if GraphQL logout fails
    }

    // Clear local storage and redirect
    localStorage.removeItem('uta.auth');
    // TODO: Tracking
    // Logout user
    // userId: null,
    // userName: null,
    // userEmail: null,
    window.location = '/login';
  }, [logoutMutation, user]);

  const menuOptions = [
    {
      name: 'Print Phonesheet (PDF)',
      MenuOptionIcon: Icons['PrinterIcon'],
      onSelect: openPdfSettings,
    },
    {
      name: 'Download Phonesheet (CSV)',
      MenuOptionIcon: Icons['TableIcon'],
      onSelect: openCsvSettings,
    },
    { name: 'Email Phonesheet', MenuOptionIcon: Icons['EmailIcon'], onSelect: emailPhonesheet },
    {
      name: 'DialLog Data Archive',
      MenuOptionIcon: Icons['PhoneOutgoingIcon'],
      onSelect: toggleArchive,
    },
    { name: 'Settings', MenuOptionIcon: Icons['SettingsIcon'], onSelect: openSettings },
    {
      name: 'Sign Out',
      MenuOptionIcon: Icons['LogOutIcon'],
      props: { hFlip: true },
      onSelect: signOut,
    },
  ];

  return (
    <div className={styles.headerContainer}>
      <div className={styles.searchColumn}>
        <SearchBar ref={searchInput} styles={styles} search={search} updateQuery={updateQuery} />
        <div className={styles.menuColumn}>
          <HeaderMenu menuOptions={menuOptions} MenuMainIcon={Icons['HamburgerMenuIcon']} />
        </div>
      </div>
      <ReactModal
        isOpen={isSettingsModalOpen}
        onRequestClose={closeSettings}
        style={{
          content: {
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            border: '0.5px solid rgba(0, 0, 0, 0.2)',
            background: `${colors.background} none repeat scroll 0% 0%`,
            borderRadius: 2,
            outline: 'currentcolor none medium',
            width: 454,
            overflow: 'unset',
            height: 'min-content',
            padding: '20px 0',
          },
        }}
      >
        <SettingsModalContent
          desk={desk}
          totalCounts={totalCounts}
          deskStatuses={deskStatuses}
          onSave={handleOnSaveSettings}
          onCancel={closeSettings}
        />
      </ReactModal>
      {/* Always render when open (so the action gives immediate feedback). While settings
          are still loading the dialog shows a loading state with confirm disabled; the `key`
          forces a fresh mount once loaded so state initializes from the real saved values. */}
      {exportFormat && (
        <ExportSettingsModal
          key={`${exportFormat}-${desk?._id}-${exportSettingsPending ? 'loading' : 'ready'}`}
          isOpen
          title={exportConfig.title}
          confirmLabel={exportConfig.confirmLabel}
          loading={exportSettingsPending}
          exporting={isExporting}
          deskStatuses={deskStatuses}
          settings={currentExportSettings}
          defaultHideFutureCalls={!!desk?.settings?.hideFutureCalls}
          onConfirm={handleExportWithSettings}
          onCancel={() => !isExporting && setExportFormat(null)}
        />
      )}
      <ArchiveSearch
        user={user}
        isOpen={isArchiveModalOpen}
        closeModal={toggleArchive}
        onPersonSelected={onCallAdd}
      />
    </div>
  );
};

const withStyles = styled({
  headerContainer: {
    background: colors.white,
    display: 'flex',
    flexDirection: 'row',
  },
  search: {
    width: '100%',
  },
  searchContainer: {
    margin: '10px',
  },
  searchInput: {
    '&&': {
      border: `1px solid ${colors.border}`,
      background: colors.white,
    },
  },
  multiselect: {
    display: 'flex',
    marginTop: 25,
    flex: 0.2,
    marginLeft: '10px',
    alignItems: 'center',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    alignItems: 'flex-start',
    width: 200,
  },
  searchColumn: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-end',
    alignItems: 'flex-end',
    margin: '10px 5px',
    justifyContent: 'center',
  },
});

const Header = withStyles(Component);

export default Header;
